import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearLastChatIf } from '@/hooks/useViewPersistence';
import {
  trackSessionSelected,
  trackSessionDeleted,
  trackAgentStopped,
  trackAgentStarted,
  trackAgentRenamed,
} from '@/analytics';
import { Urls } from '@/lib/navigation';
import type { useSessionLifecycle } from '@/hooks/useSessionLifecycle';

interface AgentActionsInput {
  session: ReturnType<typeof useSessionLifecycle>;
  setAgentError: (v: string | null) => void;
}

export const useAgentActions = (input: AgentActionsInput) => {
  const { session, setAgentError } = input;
  const { chat, chatHistory } = session;
  const navigate = useNavigate();

  const [switchingAgentId, setSwitchingAgentId] = useState<string | null>(null);

  const handleSelectAgent = useCallback(
    async (sessionId: string) => {
      trackSessionSelected(sessionId);
      const existing = chat.getChat(sessionId);
      if (existing && existing.timeline.length > 0 && existing.isRunning) {
        chat.hydrateChat(sessionId);
        void chat.reconnectIfNeeded(sessionId);
      } else {
        const timeline = await chatHistory.loadSessionTimeline(sessionId);
        chat.hydrateChat(sessionId, { timeline });
        void chat.reconnectIfNeeded(sessionId);
      }
      navigate(Urls.agent(sessionId));
    },
    [chat, chatHistory, navigate],
  );

  const handleDeleteAgent = useCallback(
    async (sessionId: string) => {
      trackSessionDeleted(sessionId);
      const wasActive = chat.activeChatId === sessionId;
      chat.removeChat(sessionId);
      clearLastChatIf(sessionId);
      await chatHistory.removeSession(sessionId);
      if (wasActive) {
        navigate(Urls.home());
      }
    },
    [chat, chatHistory, navigate],
  );

  const handleSelectAgentFromIsland = useCallback(
    async (id: string) => {
      setSwitchingAgentId(id);
      try {
        await handleSelectAgent(id);
      } catch {
        // Loading failed; user stays on agents page and can retry
      } finally {
        setSwitchingAgentId(null);
      }
    },
    [handleSelectAgent],
  );

  const handleDeleteAgentFromIsland = useCallback(
    (id: string) => handleDeleteAgent(id),
    [handleDeleteAgent],
  );

  const handleRenameAgent = useCallback(
    (id: string, newName: string) => {
      trackAgentRenamed(id);
      void chatHistory.renameSession(id, newName);
    },
    [chatHistory],
  );

  const handlePinAgent = useCallback(
    (id: string, pinned: boolean) => {
      void chatHistory.pinSession(id, pinned);
    },
    [chatHistory],
  );

  const handleStopAgent = useCallback(
    async (id: string) => {
      trackAgentStopped(id);
      setAgentError(null);
      try {
        await chatHistory.stopSession(id);
      } catch (err) {
        setAgentError(err instanceof Error ? err.message : String(err));
      }
    },
    [chatHistory, setAgentError],
  );

  const handleStartAgent = useCallback(
    async (id: string) => {
      trackAgentStarted(id);
      setAgentError(null);
      try {
        await chatHistory.startSession(id);
      } catch (err) {
        setAgentError(err instanceof Error ? err.message : String(err));
      }
    },
    [chatHistory, setAgentError],
  );

  return {
    handleSelectAgent,
    handleDeleteAgent,
    handleSelectAgentFromIsland,
    handleDeleteAgentFromIsland,
    handleRenameAgent,
    handlePinAgent,
    handleStopAgent,
    handleStartAgent,
    switchingAgentId,
  };
};
